/* Search keywords for each service, keyed by the service slug. Read only by
   the structured data in `@/lib/schema`, never rendered on the page.

   - `keyword` is the main search term and becomes the schema `serviceType`.
   - `alternateNames` are close variants of that same term.
   - `description` is written around the main keyword for search engines. It
     only restates what the service page already says.
   - `secondary` are the supporting searches. Each one is marked up as a
     sub-service inside the service's offer catalogue.

   A service added in the CMS without an entry here still appears in the
   structured data, using its CMS intro and no extra keywords. */
export type ServiceSeo = {
    keyword: string
    alternateNames: string[]
    description: string
    secondary: string[]
}

export const serviceSeo: Record<string, ServiceSeo> = {
    'extensions': {
        keyword: 'House extensions',
        alternateNames: [
            'Rear extensions',
            'Side return extensions',
            'Two-storey extensions',
            'Home extensions'
        ],
        description: 'House extension builders in South-East London and Kent, designing and building rear, side return and wrap-around extensions that add 15 to 30 square metres of living space to terraced and period homes. Every extension includes structural design, Building Control approval and Party Wall notices, then foundations, brickwork matched to the existing elevation, steel beams, a flat, pitched or lantern-lit roof, bifold or sliding glazing and a finished interior, delivered in 12 to 16 weeks on site against a fixed written quotation.',
        secondary: [
            'Rear extension',
            'Side return extension',
            'Wrap-around extension',
            'Kitchen extension',
            'Single storey extension',
            'Two-storey extension',
            'Extension foundations and underpinning',
            'Steel beam installation',
            'Roof lantern and flat roof extension',
            'Bifold and sliding door installation',
            'Party Wall notices',
            'Building Control sign-off'
        ]
    },
    'loft-conversions': {
        keyword: 'Loft conversions',
        alternateNames: [
            'Loft extensions',
            'Attic conversions'
        ],
        description: 'Loft conversion specialists in South-East London and Kent, building rear dormer, hip-to-gable and rooflight loft conversions that turn unused roof space into a bedroom with an en suite. Most loft conversions take 8 to 12 weeks on site and many fall under permitted development. Each one includes engineer-certified structural steels, a new floor structure, a staircase designed so no bedroom is lost below, a full fire strategy, insulation and plaster finishes signed off by Building Control.',
        secondary: [
            'Dormer loft conversion',
            'Rear dormer loft conversion',
            'Hip-to-gable loft conversion',
            'Rooflight loft conversion',
            'Velux loft conversion',
            'Permitted development loft conversion',
            'Loft bedroom and en suite',
            'Loft staircase installation',
            'Loft fire safety and protected escape',
            'Loft insulation to Building Regulations'
        ]
    },
    'renovations': {
        keyword: 'House renovations',
        alternateNames: [
            'Home refurbishments',
            'House refurbishments',
            'Property renovation',
            'Period property refurbishment'
        ],
        description: 'House renovation and refurbishment for Victorian, Edwardian and period homes across South-East London and Kent. Full and partial refurbishments take a tired property back to sound structure and forward to a modern specification: certificated rewiring and replumbing, new heating, damp works, breathable lime replastering, roof and chimney repairs, sash window overhaul, kitchens, bathrooms and full redecoration. Whole-house renovations typically run 10 to 20 weeks and can be phased so you stay in part of the home.',
        secondary: [
            'Full house renovation',
            'Whole house refurbishment',
            'Victorian house renovation',
            'Edwardian house renovation',
            'Period property restoration',
            'House rewiring',
            'Replumbing and heating installation',
            'Kitchen fitting',
            'Bathroom fitting',
            'Sash window repair',
            'Lime plastering',
            'Interior redecoration'
        ]
    },
    'structural-alterations': {
        keyword: 'Structural alterations',
        alternateNames: [
            'RSJ installation',
            'Structural building work',
            'Internal structural alterations'
        ],
        description: 'Structural alterations in South-East London and Kent: load-bearing wall removal, knock-throughs and open-plan conversions, RSJ and steel beam installation, goalpost frames, flitch beams and chimney breast removal in occupied homes. Every job is designed by a chartered structural engineer, properly propped, inspected and signed off by Building Control, then left plastered, square and ready to decorate, usually within 1 to 3 weeks on site.',
        secondary: [
            'Load-bearing wall removal',
            'Knock-through',
            'Open-plan living conversion',
            'RSJ installation',
            'Steel beam installation',
            'Goalpost frame installation',
            'Flitch beam installation',
            'Chimney breast removal',
            'Structural engineer calculations',
            'Building Control completion certificate'
        ]
    },
    'garage-conversions': {
        keyword: 'Garage conversions',
        alternateNames: [
            'Garage builds and conversions',
            'Garage builds'
        ],
        description: 'Garage builds and garage conversions in South-East London and Kent. We build new brick garages and convert integral and attached garages into home offices, bedrooms, gyms and utility rooms, the least expensive way to add a habitable room because the roof, walls and foundations already exist. Each garage conversion includes a new insulated floor, the door opening bricked up in matched brick or glazed, wall and roof insulation to current Building Regulations, heating, power and lighting, Building Control sign-off and a finished interior, typically in 4 to 6 weeks on site.',
        secondary: [
            'Garage conversion',
            'Integral garage conversion',
            'Attached garage conversion',
            'New garage build',
            'Brick garage construction',
            'Garage to home office conversion',
            'Garage to bedroom conversion',
            'Garage to gym conversion',
            'Garage to utility room conversion'
        ]
    },
    'finishing-works': {
        keyword: 'Building and finishing works',
        alternateNames: [
            'Plastering, carpentry and finishing works',
            'Finishing trades',
            'Interior finishing works'
        ],
        description: 'Building and finishing works across South-East London and Kent, taken on as a package in their own right or as part of a larger build: plastering and skimming, bespoke carpentry and joinery, staircases, bathroom and wet room installation, kitchen fitting, tiling, flooring and full interior and exterior decorating. Skirting and architrave are matched to the original profile, and most finishing packages take 1 to 4 weeks on site.',
        secondary: [
            'Plastering and skimming',
            'Carpentry and joinery',
            'Bespoke alcove units',
            'Staircase renovation',
            'Bathroom installation',
            'Wet room installation',
            'Kitchen installation',
            'Tiling',
            'Engineered wood flooring',
            'Underfloor heating',
            'Painting and decorating'
        ]
    },
    'drone-roof-surveys': {
        keyword: 'Roofing',
        alternateNames: [
            'Roofing services',
            'Roof repairs',
            'Roof repairs and replacement',
            'Drone roof surveys'
        ],
        description: 'Roofing contractors for South-East London and Kent: new roofs and full re-roofs in natural slate, clay and concrete tile, roof repairs, flat roof replacement in single-ply, GRP and torch-on felt, leadwork, chimney repairs and guttering for period and interwar homes. Every roofing quotation starts with an inspection, on foot where the roof can be reached safely and by drone roof survey where it cannot, with a written report and annotated images recommending whether to monitor, repair or replace.',
        secondary: [
            'New roofs',
            'Re-roofing',
            'Roof repairs',
            'Slipped and broken tile repair',
            'Flat roofs',
            'GRP fibreglass flat roofing',
            'Single-ply membrane roofing',
            'Torch-on felt roofing',
            'Leadwork and flashings',
            'Chimney repairs and repointing',
            'Guttering, fascias and soffits',
            'Drone roof inspections',
            'Roof inspection report'
        ]
    },
    'damp-proofing': {
        keyword: 'Damp proofing',
        alternateNames: [
            'Damp proofing and waterproofing',
            'Waterproofing and tanking',
            'Timber treatment',
            'Damp and timber specialists'
        ],
        description: 'Damp proofing, waterproofing and timber treatment in South-East London and Kent. Every job starts with a damp survey that finds the cause, whether rising damp, penetrating damp, a bridged cavity or a failed damp-proof course, and puts the external defect right before the wall is treated. We install chemical damp-proof courses, basement tanking and cavity drain membranes, treat wet rot, dry rot and woodworm, and re-plaster in salt-retardant or breathable lime, with a written report and guarantee paperwork for the materials used.',
        secondary: [
            'Damp survey',
            'Rising damp treatment',
            'Penetrating damp repair',
            'Chemical damp-proof course',
            'Basement waterproofing',
            'Basement tanking',
            'Cavity drain membrane',
            'Wet rot treatment',
            'Dry rot treatment',
            'Woodworm treatment',
            'Lime replastering'
        ]
    }
}
